/**
 * DegradedStateBanner Component - SPEC-019 Task 5.3
 *
 * Displays a banner when AI suggestions are in degraded state.
 * Shows the degradation reason and optional hint for improvement.
 *
 * @module features/ai-button/components/DegradedStateBanner
 * @see SPEC-019 Requirements 6.1, 6.2, 6.3, 6.5
 */

import { memo } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../lib/cn';
import { useTypedTranslation, type TranslationOptions } from '../../../i18n';
import type { TranslationKey } from '../../../i18n/types';

// ============================================================================
// Types
// ============================================================================

/** Possible degradation reasons from API */
export type DegradeReason =
  | 'vector_down'
  | 'graph_empty'
  | 'quota'
  | 'other'
  | 'vector_not_configured'
  | 'vector_connection_failed'
  | 'insufficient_docs';

/** Hint structure from API */
export interface SuggestHint {
  key: string;
  params?: Record<string, unknown>;
}

// ============================================================================
// CVA Variants
// ============================================================================

/**
 * DegradedStateBanner variants using CVA
 */
export const degradedBannerVariants = cva(
  // Base styles
  ['flex items-start gap-3 p-3', 'rounded-md border', 'text-sm'],
  {
    variants: {
      severity: {
        warning: ['bg-yellow-50', 'border-yellow-200', 'text-yellow-800'],
        error: ['bg-red-50', 'border-red-200', 'text-red-800'],
        info: ['bg-blue-50', 'border-blue-200', 'text-blue-800'],
      },
    },
    defaultVariants: {
      severity: 'warning',
    },
  }
);

export type DegradedBannerVariants = VariantProps<typeof degradedBannerVariants>;

// ============================================================================
// Props
// ============================================================================

export interface DegradedStateBannerProps extends DegradedBannerVariants {
  /** The reason for degradation */
  degradeReason: DegradeReason;
  /** Optional hint for improving suggestions */
  hint?: SuggestHint;
  /** Whether the banner can be dismissed */
  dismissible?: boolean;
  /** Callback when banner is dismissed */
  onDismiss: () => void;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Map degradeReason to i18n key
 */
function getReasonI18nKey(reason: DegradeReason): TranslationKey {
  const mapping: Record<DegradeReason, TranslationKey> = {
    vector_down: 'ai.degraded.vectorDown',
    graph_empty: 'ai.degraded.graphEmpty',
    quota: 'ai.degraded.quota',
    other: 'ai.degraded.unknown',
    insufficient_docs: 'ai.degraded.insufficientDocs',
    vector_not_configured: 'ai.degraded.vectorNotConfigured',
    vector_connection_failed: 'ai.degraded.vectorConnectionFailed',
  };
  return mapping[reason] ?? ('ai.degraded.unknown' as TranslationKey);
}

/**
 * Map degradeReason to severity level
 */
function getSeverity(reason: DegradeReason): 'warning' | 'error' | 'info' {
  switch (reason) {
    case 'quota':
    case 'vector_down':
    case 'vector_connection_failed':
      return 'error';
    case 'graph_empty':
    case 'insufficient_docs':
      return 'info';
    case 'vector_not_configured':
    case 'other':
    default:
      return 'warning';
  }
}

/**
 * Get icon based on severity
 */
function SeverityIcon({ severity }: { severity: 'warning' | 'error' | 'info' }) {
  const iconClass = 'w-5 h-5 flex-shrink-0';

  if (severity === 'error') {
    return (
      <svg
        className={cn(iconClass, 'text-red-500')}
        fill="currentColor"
        viewBox="0 0 20 20"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
          clipRule="evenodd"
        />
      </svg>
    );
  }

  if (severity === 'info') {
    return (
      <svg
        className={cn(iconClass, 'text-blue-500')}
        fill="currentColor"
        viewBox="0 0 20 20"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
          clipRule="evenodd"
        />
      </svg>
    );
  }

  // Warning (default)
  return (
    <svg
      className={cn(iconClass, 'text-yellow-500')}
      fill="currentColor"
      viewBox="0 0 20 20"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
        clipRule="evenodd"
      />
    </svg>
  );
}

// ============================================================================
// Component
// ============================================================================

/**
 * DegradedStateBanner Component
 *
 * Displays degradation status with:
 * - Severity-based styling (warning/error/info)
 * - Translated reason message
 * - Optional hint for improvement
 * - Dismissible option
 *
 * @see SPEC-019 Requirements 6.1, 6.2, 6.3, 6.5
 */
export const DegradedStateBanner = memo(function DegradedStateBanner({
  degradeReason,
  hint,
  dismissible = false,
  severity: severityProp,
  onDismiss,
}: DegradedStateBannerProps) {
  const { t } = useTypedTranslation();

  const severity = severityProp ?? getSeverity(degradeReason);
  const reasonMessage = t(getReasonI18nKey(degradeReason));
  const hintMessage = hint
    ? t(hint.key as TranslationKey, hint.params as TranslationOptions)
    : null;

  return (
    <div
      className={cn(degradedBannerVariants({ severity }))}
      data-testid="degraded-banner"
      data-reason={degradeReason}
      role="alert"
    >
      <SeverityIcon severity={severity} />

      <div className="flex-1 min-w-0">
        <p className="font-medium">{reasonMessage}</p>
        {hintMessage && (
          <p className="mt-1 text-xs opacity-80" data-testid="hint-message">
            {hintMessage}
          </p>
        )}
      </div>

      {dismissible && (
        <button
          type="button"
          data-testid="dismiss-button"
          className={cn(
            'flex-shrink-0 p-1 rounded',
            'hover:bg-black/5 transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-offset-1',
            severity === 'error' && 'focus:ring-red-500',
            severity === 'warning' && 'focus:ring-yellow-500',
            severity === 'info' && 'focus:ring-blue-500'
          )}
          onClick={onDismiss}
          aria-label={t('ai.degraded.dismiss')}
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      )}
    </div>
  );
});
